/**
 * O que faz: Página para solicitar recuperação de senha via email
 * Onde usa: Rota /esqueci-senha acessível da página de login
 * Conexões: AuthContext (resetPassword), Supabase Auth
 * Tabelas: N/A (apenas auth do Supabase)
 * Autor: IA
 * Data: 04/02/2026
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

/**
 * Página "Esqueci Minha Senha"
 * 
 * Permite que o usuário solicite um email de recuperação de senha.
 * O Supabase enviará um link mágico para o email cadastrado.
 * 
 * Fluxo:
 * 1. Usuário digita o email
 * 2. Sistema envia email com link de recuperação
 * 3. Usuário é redirecionado para página de confirmação
 */
export const ForgotPasswordPage = () => {
    const { resetPassword } = useAuth();

    // Estados do formulário
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const [error, setError] = useState('');

    // Validar email
    const isValidEmail = (email: string) => {
        const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
        return emailRegex.test(email);
    };

    // Submeter formulário
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        // Validação do email
        if (!email.trim()) {
            setError('Email é obrigatório');
            return;
        }

        if (!isValidEmail(email)) {
            setError('Digite um email válido');
            return;
        }

        try {
            setLoading(true);
            await resetPassword(email.trim());
            setEmailSent(true);
        } catch (err: any) {
            // Erro já tratado no AuthContext, mas podemos capturar aqui para UI
            console.error('Erro ao solicitar recuperação:', err);
        } finally {
            setLoading(false);
        }
    };

    // Tela de sucesso após envio do email
    if (emailSent) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-green-100 px-4">
                <Card className="w-full max-w-md rounded-2xl shadow-lg border-green-200">
                    <CardContent className="pt-8 pb-6 text-center">
                        {/* Ícone de sucesso animado */}
                        <div className="flex items-center justify-center w-16 h-16 bg-green-100 mx-auto mb-6 rounded-full">
                            <CheckCircle className="w-10 h-10 text-green-600" />
                        </div>

                        <CardTitle className="text-2xl font-bold text-gray-900 mb-3">
                            Email enviado!
                        </CardTitle>

                        <CardDescription className="text-base mb-6">
                            Enviamos um link de recuperação para{' '}
                            <span className="font-semibold text-gray-700">{email}</span>.
                            <br /><br />
                            Verifique sua caixa de entrada e spam. O link expira em 1 hora.
                        </CardDescription>

                        {/* Botão para reenviar */}
                        <Button
                            variant="outline"
                            className="w-full rounded-xl py-3 mb-4 border-green-300 hover:bg-green-50"
                            onClick={() => {
                                setEmailSent(false);
                                setEmail('');
                            }}
                        >
                            Tentar outro email
                        </Button>

                        {/* Link para voltar ao login */}
                        <Link
                            to="/login"
                            className="inline-flex items-center gap-2 text-sm text-green-600 hover:text-green-700 font-medium transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Voltar ao login
                        </Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Formulário de solicitação
    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-green-100 px-4">
            <Card className="w-full max-w-md rounded-2xl shadow-lg border-green-200">
                <CardHeader className="text-center">
                    {/* Ícone do header */}
                    <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-green-400 to-green-500 mx-auto mb-4 shadow-md rounded-full">
                        <Mail className="w-8 h-8 text-white" />
                    </div>

                    <CardTitle className="text-2xl font-bold text-gray-900">
                        Esqueceu sua senha?
                    </CardTitle>

                    <CardDescription>
                        Digite seu email e enviaremos um link para você criar uma nova senha.
                    </CardDescription>
                </CardHeader>

                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Campo Email */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                Email
                            </label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    if (error) setError('');
                                }}
                                placeholder="Digite seu email cadastrado"
                                className={`rounded-xl transition-all duration-200 ${error
                                        ? 'border-red-500 focus:border-red-500'
                                        : 'border-green-200 focus:border-green-400 focus:ring-green-100'
                                    }`}
                                autoComplete="email"
                                autoFocus
                            />
                            {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
                        </div>

                        {/* Botão de Enviar */}
                        <Button
                            type="submit"
                            className="w-full bg-green-600 hover:bg-green-700 text-white rounded-xl py-3 font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Enviando...
                                </>
                            ) : (
                                'Enviar link de recuperação'
                            )}
                        </Button>
                    </form>

                    {/* Links de navegação */}
                    <div className="mt-6 text-center space-y-3">
                        <p className="text-sm text-gray-600">
                            Lembrou sua senha?{' '}
                            <Link
                                to="/login"
                                className="text-green-600 hover:text-green-700 font-medium transition-colors"
                            >
                                Fazer login
                            </Link>
                        </p>

                        <Link
                            to="/"
                            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-green-600 transition-colors rounded-lg px-3 py-2 hover:bg-green-50"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Voltar ao início
                        </Link>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default ForgotPasswordPage;
